import { forwardRef } from "react";

import { useHistory } from "react-router-dom";

import Avatar from "@/components/Avatar";
import Box from "@/components/Box";
import { ExternalTextLink } from "@/components/Links";
import { CMP_LEARNING_HUB_WEBINAR_URL } from "@/core/urls";
import useCmpRelatedUrl from "@/hooks/organisations/useCmpRelatedUrl";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";

export function HostProfile({ occupation, title, name, avatarUrl }) {
  return (
    <Group alignItems="flex-end" gap={10} minWidth={0}>
      {avatarUrl && (
        <Box flexShrink={0}>
          <Avatar src={avatarUrl} size={70} />
        </Box>
      )}
      <Stack gap={0} minWidth={0}>
        <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
          Keynote speaker
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} overflowWrap="anywhere">
          {title ? `${title} ${name}` : name}
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={14} overflowWrap="anywhere">
          {occupation}
        </Box>
      </Stack>
    </Group>
  );
}

const DEFAULT_STACKED = [true, true, false];

function normalizeStacked(value) {
  if (value === undefined) {
    return DEFAULT_STACKED;
  }
  return Array.isArray(value) ? value : [value];
}

/** Per-breakpoint values from the page's `stacked` array (styled-system responsive arrays). */
function pickFromStacked(stacked, whenStacked, whenNotStacked) {
  return normalizeStacked(stacked).map(isStackedAtBreakpoint =>
    isStackedAtBreakpoint ? whenStacked : whenNotStacked
  );
}

function WebinarQualificationBadge({ badgeName, points, fontSize }) {
  return (
    <Box
      flexShrink={0}
      borderRadius="50%"
      minWidth={52}
      minHeight={52}
      width="auto"
      maxWidth={88}
      px={8}
      py={6}
      borderWidth={2}
      borderStyle="solid"
      borderColor="dark"
      backgroundColor="transparent"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={0}
      fontFamily="gilroyBold"
      fontSize={fontSize}
    >
      <Box
        fontSize={12}
        lineHeight="110%"
        textTransform="uppercase"
        textAlign="center"
        whiteSpace="normal"
        overflowWrap="anywhere"
      >
        {badgeName}
      </Box>
      <Box fontSize={24} lineHeight="100%">
        {points}
      </Box>
    </Box>
  );
}

const WebinarCard = forwardRef(
  (
    {
      id,
      title,
      shortDescription,
      host,
      startTime,
      endTime,
      videoUrl,
      slug,
      webinarBadges,
      stacked = DEFAULT_STACKED,
      date: dateProps,
      content: contentProps,
      header: headerProps,
      footer: footerProps,
      button: buttonProps
    },
    ref
  ) => {
    const history = useHistory();
    const { resolvedUrls } = useCmpRelatedUrl();

    const {
      title: titleProps,
      badges: badgesGroupProps,
      badge: badgeProps,
      ...headerGroupProps
    } = headerProps ?? {};

    const { description: descriptionProps, ...contentStackProps } = contentProps ?? {};

    const { host: hostBoxProps, ...footerGroupProps } = footerProps ?? {};

    return (
      <Group
        flexDirection={pickFromStacked(stacked, "column", "row")}
        alignItems="stretch"
        height="100%"
        borderRadius={20}
        cursor="pointer"
        ref={ref}
        onClick={() => {
          history.push(resolvedUrls[CMP_LEARNING_HUB_WEBINAR_URL].replace(":slug", slug));
        }}
      >
        <Stack
          bg="partners"
          py={32}
          px={20}
          borderTopLeftRadius={20}
          borderBottomLeftRadius={pickFromStacked(stacked, 0, 20)}
          borderTopRightRadius={pickFromStacked(stacked, 20, 0)}
          borderBottomRightRadius={0}
          justifyContent="center"
          gap={0}
          alignItems="center"
          alignSelf="stretch"
          width={pickFromStacked(stacked, "100%", 120)}
          minWidth={pickFromStacked(stacked, "100%", 120)}
          maxWidth={pickFromStacked(stacked, "100%", 120)}
          {...dateProps}
        >
          <Box fontFamily="gilroyMedium" fontSize={[18]}>
            {new Date(startTime).toLocaleDateString("en-US", {
              month: "short"
            })}
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={[60]}>
            {new Date(startTime).toLocaleDateString("en-US", {
              day: "2-digit"
            })}
          </Box>
        </Stack>
        <Stack
          px={[24, 24, 40]}
          py={24}
          borderTopLeftRadius={0}
          borderBottomLeftRadius={pickFromStacked(stacked, 20, 0)}
          borderTopRightRadius={pickFromStacked(stacked, 0, 20)}
          borderBottomRightRadius={20}
          flex="1 1 0"
          minWidth={0}
          alignSelf="stretch"
          height="100%"
          bg="white"
          {...contentStackProps}
        >
          <Stack gap={16} flex="1 1 auto">
            <Group
              flexDirection={pickFromStacked(stacked, "column", "row")}
              justifyContent={pickFromStacked(stacked, null, "space-between")}
              alignItems={pickFromStacked(stacked, null, "flex-end")}
              gap={16}
              {...headerGroupProps}
            >
              <Box>
                <Box fontFamily="gilroyMedium" fontSize={[18]} color="anchorBlue">
                  {new Date(startTime).toLocaleDateString("en-US", {
                    month: "short",
                    day: "2-digit"
                  })}{" "}
                  &bull;{" "}
                  {new Date(startTime).toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "numeric"
                  })}{" "}
                  {endTime ? "-" : null}{" "}
                  {!!endTime &&
                    new Date(endTime).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "numeric"
                    })}
                </Box>
                <Box
                  fontFamily="gilroyBold"
                  fontSize={[18, 18, 24]}
                  style={{
                    lineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                    display: "-webkit-box"
                  }}
                  {...titleProps}
                >
                  {title}
                </Box>
              </Box>
              {!!webinarBadges?.length && (
                <Group
                  gap={6}
                  flexShrink={0}
                  alignItems="flex-end"
                  justifyContent={pickFromStacked(stacked, null, "flex-end")}
                  {...badgesGroupProps}
                >
                  {webinarBadges.map(wb => (
                    <WebinarQualificationBadge
                      key={wb.id}
                      badgeName={wb.badge.name}
                      points={wb.points}
                      fontSize={[14, 14, 16]}
                      {...badgeProps}
                    />
                  ))}
                </Group>
              )}
            </Group>
            <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} {...descriptionProps}>
              {shortDescription}
            </Box>
          </Stack>
          <Group
            alignItems={pickFromStacked(stacked, "flex-start", "flex-end")}
            justifyContent="space-between"
            flexWrap="wrap"
            gap={20}
            width="100%"
            mt="auto"
            pt={20}
            {...footerGroupProps}
          >
            <Box flex="0 1 auto" minWidth={0} {...hostBoxProps}>
              <HostProfile
                name={host?.fullName}
                title={host?.title}
                occupation={host?.occupation}
                avatarUrl={host?.avatarUrl}
              />
            </Box>
            <ButtonV2
              as={ExternalTextLink}
              color="dark"
              rightIcon={<ChevronComponent />}
              underline={false}
              href={resolvedUrls[CMP_LEARNING_HUB_WEBINAR_URL].replace(":slug", slug)}
              size={["sm", "sm", "md"]}
              flexShrink={0}
              alignSelf="flex-start"
              sx={{
                pt: 20,
                pb: 20
              }}
              {...buttonProps}
            >
              {new Date() > new Date(startTime) ? "watch now" : "join us"}
            </ButtonV2>
          </Group>
        </Stack>
      </Group>
    );
  }
);

export default WebinarCard;
